import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth'
import { Icon } from './shared'
import { useT } from './i18n'
import {
  LOCK_ON_KEY, LOCK_PIN_KEY, UNLOCKED_KEY,
  applyTheme, isPin, readStore, readTheme, removeStore, writeStore,
} from './AppSettings'
import '../../mobile.css'

/* The unlock screen.
 *
 * Like the sign-in screen, this renders its own phone frame: it is a gate in
 * front of the mobile router, so the shared store the other screens read from
 * does not exist yet when it paints.
 *
 * What this protects is narrow and worth being honest about. The session cookie
 * is untouched, so the PIN is not a second factor for the clinic login. It stops
 * the next person who picks up a shared tablet from reading the last patient's
 * record, and nothing more. */

/** How long a successful unlock lasts. Long enough to walk a patient through a
 *  consultation, short enough that a tablet left on the counter re-locks. */
export const UNLOCK_GRACE_MS = 15 * 60 * 1000

/**
 * Whether the router should divert to this screen.
 *
 * Returns false whenever anything is missing or unreadable, including when
 * storage throws: a lock that cannot be opened because its own state is broken
 * would make the app unusable, which is far worse than an unlocked tablet.
 */
export function isLocked(): boolean {
  try {
    if (localStorage.getItem(LOCK_ON_KEY) !== '1') return false
    // The flag is on but no usable PIN was ever stored, so there is nothing to
    // enter and no way back in. Treat that as unlocked.
    if (!isPin(localStorage.getItem(LOCK_PIN_KEY) ?? '')) return false

    const raw = localStorage.getItem(UNLOCKED_KEY)
    if (raw === null) return true
    const at = Number(raw)
    if (!Number.isFinite(at) || at <= 0) return true
    return Date.now() - at > UNLOCK_GRACE_MS
  } catch {
    return false
  }
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']

export default function Lock() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { t } = useT()
  const [pin, setPin] = useState('')
  const [bad, setBad] = useState(false)
  const timer = useRef<number | null>(null)

  // The lock screen paints its own frame, so it has to apply the stored
  // appearance itself: without this it would flash white in a dark room.
  useEffect(() => applyTheme(readTheme()), [])

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
  }, [])

  const check = useCallback((entered: string) => {
    const stored = readStore(LOCK_PIN_KEY) ?? ''
    if (isPin(stored) && entered === stored) {
      writeStore(UNLOCKED_KEY, String(Date.now()))
      navigate('/m', { replace: true })
      return
    }
    setBad(true)
    // Held long enough for the shake to finish before the dots reset.
    timer.current = window.setTimeout(() => {
      setPin('')
      setBad(false)
      timer.current = null
    }, 480)
  }, [navigate])

  // Checked outside a state updater on purpose: an updater runs twice under
  // StrictMode, and navigating or starting a timer twice from one tap is worse
  // than recreating this callback on every keystroke.
  const press = useCallback((digit: string) => {
    if (bad || pin.length >= 4) return
    const entered = pin + digit
    setPin(entered)
    if (entered.length === 4) check(entered)
  }, [bad, pin, check])

  const back = useCallback(() => {
    setBad(false)
    setPin((prev) => prev.slice(0, -1))
  }, [])

  // A tablet on a desk often has a keyboard attached, and typing the PIN is
  // quicker than tapping it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key >= '0' && e.key <= '9' && e.key.length === 1) { press(e.key); return }
      if (e.key === 'Backspace') back()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [press, back])

  async function out() {
    try {
      await signOut()
    } catch {
      /* Already signed out, or the server is unreachable. Either way the next
         screen is the sign-in form. */
    }
    removeStore(UNLOCKED_KEY)
    navigate('/m/login', { replace: true })
  }

  return (
    <div className="mapp-desk">
      <div className="mapp">
        <div className="mapp-screen no-tabs">
          <div className="m-lock">
            <div className="m-login-mark"><Icon name="shield" size={30} /></div>
            <h1>{t('shell.enterPin')}</h1>
            <p className="m-note">
              {user?.fullName
                ? t('shell.stillSignedIn', { name: user.fullName })
                : t('shell.deviceLocked')}
            </p>

            <div className={`m-pinrow${bad ? ' m-shake' : ''}`} role="status" aria-label={t('shell.pinEntry')}>
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={`m-pindot${bad ? ' is-bad' : i < pin.length ? ' is-on' : ''}`}
                />
              ))}
            </div>

            <p className={`m-note${bad ? ' warn' : ''}`} aria-live="polite">
              {bad ? t('shell.pinWrong') : t('shell.fourDigits')}
            </p>

            <div className="m-keypad">
              {KEYS.map((k) => (
                <button key={k} type="button" className="m-key" onClick={() => press(k)}>{k}</button>
              ))}
              <span />
              <button type="button" className="m-key" onClick={() => press('0')}>0</button>
              <button
                type="button" className="m-key ghost" aria-label={t('shell.deleteDigit')}
                onClick={back} disabled={pin.length === 0}
              >
                <Icon name="back" size={22} />
              </button>
            </div>

            <button type="button" className="m-link-btn" onClick={() => void out()}>
              <Icon name="logout" size={16} /> {t('shell.signOutInstead')}
            </button>

            <p className="m-note">
              {t('shell.forgotPin')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
